import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setPageFilterValue,
  setPageFilterValues,
  setAutoTriggeredValue,
  resetPageFilters
} from '../store/slices/filters';

const EMPTY_VALUES = Object.freeze({});
const EMPTY_TRIGGERED = Object.freeze({});

/**
 * Read/write persisted filters for a given page key.
 *
 * @param {string} pageKey - stable identifier for the page/table this hook
 *   is managing filters for (e.g. route pathname, or pathname + table title
 *   when a page hosts more than one filterable table).
 */
const usePageFilterPersistence = (pageKey) => {
  const dispatch = useDispatch();

  const filterValues = useSelector((state) =>
    (pageKey ? state.filters?.[pageKey]?.values : null)
  ) || EMPTY_VALUES;

  const autoTriggered = useSelector((state) =>
    (pageKey ? state.filters?.[pageKey]?.autoTriggered : null)
  ) || EMPTY_TRIGGERED;

  const setFilter = useCallback((key, value) => {
    if (!pageKey) return;
    dispatch(setPageFilterValue({ pageKey, key, value }));
  }, [dispatch, pageKey]);

  const setFilters = useCallback((values) => {
    if (!pageKey) return;
    dispatch(setPageFilterValues({ pageKey, values }));
  }, [dispatch, pageKey]);

  // Call once you've actually performed the auto-triggered action (e.g.
  // clicked search / called the filter API) for a given filter `key`,
  // passing the value/signature it was triggered for. Future effects
  // should compare against this before triggering again.
  const markAutoTriggered = useCallback((key, value) => {
    if (!pageKey) return;
    dispatch(setAutoTriggeredValue({ pageKey, key, value }));
  }, [dispatch, pageKey]);

  const clearPageFilters = useCallback(() => {
    if (!pageKey) return;
    dispatch(resetPageFilters({ pageKey }));
  }, [dispatch, pageKey]);

  return { filterValues, autoTriggered, setFilter, setFilters, markAutoTriggered, clearPageFilters };
};

export default usePageFilterPersistence;